import { PRIVACY_URL } from './dgpt'
import type { FaqItem } from '../components/ui/FaqList'

export type VcCapability = {
  id: string
  eyebrow: string
  title: string
  body: string
  points: string[]
  visual: 'thesis' | 'radar' | 'memo' | 'pipeline'
}

export const VC_CAPABILITIES: VcCapability[] = [
  {
    id: 'thesis-fit',
    eyebrow: 'Thesis fit',
    title: 'Every company assessed against your mandate.',
    body: 'Set your thesis once — sector, stage, geography, check size and the questions your team actually asks — and the Thesis Fit Agent assesses each company criterion by criterion, with the evidence behind each call.',
    points: ['Fit by criterion, not one opaque number', 'Evidence linked to the deck or document', 'Missing information listed separately'],
    visual: 'thesis',
  },
  {
    id: 'deal-radar',
    eyebrow: 'Deal Radar',
    title: 'Your inbox shows you the deals you already know.',
    body: 'Deal Radar helps surface the ones you don’t. Match companies from StartupFuel’s global ecosystem against your investment thesis.',
    points: ['Matched against your thesis', 'Reviewed by your team before outreach', 'Added to the same pipeline'],
    visual: 'radar',
  },
  {
    id: 'structured-diligence',
    eyebrow: 'Structured diligence',
    title: 'The same structure for every deal.',
    body: 'Research, financial, market and competitive agents produce consistent reports — tearsheet, market, business risk, metrics, comparables and gap analysis — so analysis is comparable across companies.',
    points: ['Consistent report structure', 'Sourced strengths and risks', 'Open questions for founder calls'],
    visual: 'memo',
  },
  {
    id: 'pipeline-ic',
    eyebrow: 'Pipeline & IC',
    title: 'From screening to investment committee in one pipeline.',
    body: 'Deals move through your stages with their documents, findings and fit scores attached. Memo and Committee agents assemble IC materials in your template for partners to review.',
    points: ['Fit score on every deal', 'IC memo drafts in your format', 'Decisions recorded with the deal'],
    visual: 'pipeline',
  },
]

export const VC_EXTRA_CAPABILITIES = [
  {
    title: 'IC outputs',
    body: 'Investment memo and IC template agents turn structured findings into documents your partners can challenge and finish.',
  },
  {
    title: 'Portfolio monitoring',
    body: 'Company Update and Portfolio Monitoring agents collect updates after the investment and structure KPIs over time.',
  },
  {
    title: 'Founder communication',
    body: 'Gap Analysis and Founder Communication agents draft follow-up questions and requests for your team to send.',
  },
] as const

export const VC_FAQ: FaqItem[] = [
  {
    question: 'What does DiligenceGPT™ do for venture teams?',
    answer:
      'It runs the repetitive work around the investment decision — intake, thesis screening, research, founder follow-up, diligence, IC preparation and portfolio monitoring — so your team can focus on judgment.',
  },
  {
    question: 'What do I need to get started?',
    answer: 'A pitch deck is enough to start. Adding a financial model and data room documents gives the agents more to work with and reduces open questions.',
  },
  {
    question: 'Can I use my fund’s investment criteria?',
    answer:
      'Yes. You set your thesis — sectors, stage, geography, check size and other requirements — and companies are assessed against those criteria rather than a generic framework.',
  },
  {
    question: 'Can I see a sample investment memo?',
    answer: 'Yes. The sample memo uses a fictional company to show the structure, sources and open questions you’d receive. No email address is required.',
    link: { label: 'Read the sample memo', href: '/sample-memo' },
  },
  {
    question: 'Who makes the investment decision?',
    answer:
      'Your team. Treat agent outputs as a first draft: check findings against sources, confirm material figures with the company and apply your judgment before anything goes to IC.',
  },
  {
    question: 'How is company information handled?',
    answer:
      'As currently published on dgpt.io: customer deal data is not used to train our models or third-party models, workspaces are tenant-isolated, and a SOC 2 audit is in progress (not yet certified).',
    link: { label: 'Privacy Policy', href: PRIVACY_URL },
  },
]

/** Fictional companies for the Deal Radar preview. */
export const RADAR_EXAMPLES = [
  { name: 'Northwind Grid', sector: 'Climate · Energy storage', stage: 'Seed', fit: 86 },
  { name: 'Ledgerline', sector: 'Fintech · B2B payments', stage: 'Series A', fit: 79 },
  { name: 'Corvo Health', sector: 'Health · Care coordination', stage: 'Seed', fit: 72 },
  { name: 'Atlas Robotics', sector: 'Industrial · Automation', stage: 'Pre-seed', fit: 64 },
] as const
